/**
 * Hero Trouvé Chat — chip templates + intent → onboarding-goal mapping.
 *
 * Brand rule (iter 79, Jun 25 2026): all chip-based responses are
 * pre-written here so a chip tap is INSTANT (no LLM round-trip).
 * Free-text input gets an LLM fallback via /api/demo/trouve/ask;
 * chip taps never hit the network. The founder reviewed and locked
 * the copy in the iter 79 brief — do not edit the responses without
 * her approval.
 *
 * Structure:
 *   TOPICS                       — Turn 0 chip row (6 topics)
 *   TURN1_RESPONSES[topic_id]    — Turn 1 acknowledgement + 3 mini chips
 *   TURN2_RESPONSES[topic_id]    — Turn 2 tiny tip + signup transition
 *                                  (one tip per topic — works for any
 *                                  of the 3 mini chips so we don't
 *                                  combinatorially explode the copy)
 *   topicToGoal(topic_id)        — Maps a hero topic to the existing
 *                                  onboarding `preferences.goal` value
 *                                  so onboarding can skip the goal
 *                                  question entirely.
 */

export const HERO_TROUVE_TOPICS = [
  {
    id: "soften_sculpt",
    emoji: "✨",
    labelKey: "heroTrouve.topics.softenSculpt",
    fallback: "I want to soften my features",
  },
  {
    id: "sensitive_skin",
    emoji: "💧",
    labelKey: "heroTrouve.topics.sensitiveSkin",
    fallback: "My skin is sensitive",
  },
  {
    id: "undertone",
    emoji: "🌸",
    labelKey: "heroTrouve.topics.undertone",
    fallback: "I'm new to makeup",
  },
  {
    id: "mature_skin",
    emoji: "🌹",
    labelKey: "heroTrouve.topics.matureSkin",
    fallback: "Mature skin",
  },
  {
    id: "everyday_natural",
    emoji: "💄",
    labelKey: "heroTrouve.topics.everydayNatural",
    fallback: "Everyday makeup",
  },
  {
    id: "beard_shadow",
    emoji: "💋",
    labelKey: "heroTrouve.topics.beardShadow",
    fallback: "Help me find my routine",
  },
];

export const HERO_TROUVE_TURN1 = {
  beard_shadow: {
    bodyKey: "heroTrouve.turn1.beardShadow.body",
    chips: [
      { id: "showing_through_foundation",
        labelKey: "heroTrouve.turn1.beardShadow.chip1" },
      { id: "color_corrector",
        labelKey: "heroTrouve.turn1.beardShadow.chip2" },
      { id: "lasts_all_day",
        labelKey: "heroTrouve.turn1.beardShadow.chip3" },
    ],
  },
  soften_sculpt: {
    bodyKey: "heroTrouve.turn1.softenSculpt.body",
    chips: [
      { id: "softer_everyday",
        labelKey: "heroTrouve.turn1.softenSculpt.chip1" },
      { id: "lifted_shape",
        labelKey: "heroTrouve.turn1.softenSculpt.chip2" },
      { id: "feminine_contour",
        labelKey: "heroTrouve.turn1.softenSculpt.chip3" },
    ],
  },
  undertone: {
    bodyKey: "heroTrouve.turn1.undertone.body",
    chips: [
      { id: "foundation_orange",
        labelKey: "heroTrouve.turn1.undertone.chip1" },
      { id: "foundation_gray",
        labelKey: "heroTrouve.turn1.undertone.chip2" },
      { id: "no_idea",
        labelKey: "heroTrouve.turn1.undertone.chip3" },
    ],
  },
  sensitive_skin: {
    bodyKey: "heroTrouve.turn1.sensitiveSkin.body",
    chips: [
      { id: "fragrance",
        labelKey: "heroTrouve.turn1.sensitiveSkin.chip1" },
      { id: "breakouts_irritation",
        labelKey: "heroTrouve.turn1.sensitiveSkin.chip2" },
      { id: "ingredient_confusion",
        labelKey: "heroTrouve.turn1.sensitiveSkin.chip3" },
    ],
  },
  mature_skin: {
    bodyKey: "heroTrouve.turn1.matureSkin.body",
    chips: [
      { id: "foundation_settling",
        labelKey: "heroTrouve.turn1.matureSkin.chip1" },
      { id: "more_glow",
        labelKey: "heroTrouve.turn1.matureSkin.chip2" },
      { id: "lifting_the_face",
        labelKey: "heroTrouve.turn1.matureSkin.chip3" },
    ],
  },
  everyday_natural: {
    bodyKey: "heroTrouve.turn1.everydayNatural.body",
    chips: [
      { id: "work_errands",
        labelKey: "heroTrouve.turn1.everydayNatural.chip1" },
      { id: "date_dinner",
        labelKey: "heroTrouve.turn1.everydayNatural.chip2" },
      { id: "barely_there",
        labelKey: "heroTrouve.turn1.everydayNatural.chip3" },
    ],
  },
};

export const HERO_TROUVE_TURN2 = {
  beard_shadow: "heroTrouve.turn2.beardShadow",
  soften_sculpt: "heroTrouve.turn2.softenSculpt",
  undertone: "heroTrouve.turn2.undertone",
  sensitive_skin: "heroTrouve.turn2.sensitiveSkin",
  mature_skin: "heroTrouve.turn2.matureSkin",
  everyday_natural: "heroTrouve.turn2.everydayNatural",
};

/**
 * Maps a hero topic onto the existing onboarding `preferences.goal`
 * enum so Onboarding.jsx can skip the goal question. Values mirror
 * those defined in /app/frontend/src/pages/Onboarding.jsx (`soft`,
 * `everyday`, `glam`, `explore`). Topics that don't map cleanly
 * default to `everyday` — the safest "real life, not a glam moment"
 * starting point.
 *
 * NOTE (iter 82, Feb 27 2026): the topics `beard_shadow` and
 * `undertone` were repurposed in copy (see HERO_TROUVE_TOPICS) but
 * the IDs were kept intact to avoid breaking pre-existing analytics
 * events and saved hero intent blobs. The mapping below reflects the
 * NEW conceptual intent:
 *   • `beard_shadow` is now "Help me find my routine" → "everyday"
 *   • `undertone`    is now "I'm new to makeup"        → "soft"
 */
export const topicToGoal = (topicId) => {
  switch (topicId) {
    case "beard_shadow":
      // "Help me find my routine" — daily life starting point.
      return "everyday";
    case "soften_sculpt":
      return "soft";
    case "undertone":
      // "I'm new to makeup" — gentle, beginner-friendly default.
      return "soft";
    case "sensitive_skin":
      return "everyday";
    case "mature_skin":
      return "soft";
    case "everyday_natural":
      return "soft";
    default:
      return "everyday";
  }
};
